'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { Globe } from 'lucide-react';
import {
  NestedSheet,
  NestedSheetContent,
  NestedSheetHeader,
  NestedSheetTitle,
  NestedSheetBody,
} from '@/shared/ui';
import type { EnvironmentSetting } from '@/shared/constants';
import {
  EnvironmentContent,
  getEnvironmentSubtitle,
} from '../settings-content/EnvironmentContent';
import { SettingsSectionRow } from './SettingsSection';
import { assistantTargets, sectionTarget } from '@/shared/assistant';

interface EnvironmentSectionProps {
  environmentSetting: EnvironmentSetting;
  onEnvironmentChange: (setting: EnvironmentSetting) => void;
  hasChanges?: boolean;
}

export function EnvironmentSection({
  environmentSetting,
  onEnvironmentChange,
  hasChanges = false,
}: EnvironmentSectionProps) {
  const t = useTranslations('render.settings.environment');
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  const subtitle = getEnvironmentSubtitle(environmentSetting, t);

  return (
    <>
      <SettingsSectionRow
        assistantTarget={assistantTargets(sectionTarget('environment'))}
        icon={Globe}
        title={t('title')}
        subtitle={subtitle}
        hasChanges={hasChanges}
        onClick={() => setIsSheetOpen(true)}
      />

      <NestedSheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
        <NestedSheetContent>
          <NestedSheetHeader>
            <NestedSheetTitle>{t('title')}</NestedSheetTitle>
          </NestedSheetHeader>

          <NestedSheetBody className="px-4">
            <EnvironmentContent
              value={environmentSetting}
              onChange={onEnvironmentChange}
              variant="compact"
              closeOnSelect={() => setIsSheetOpen(false)}
            />
          </NestedSheetBody>
        </NestedSheetContent>
      </NestedSheet>
    </>
  );
}
